import { getDb, StoredPacket, StoredNode } from './db';
import { SessionManager } from './sessionManager';
import { LocationTracker, GeoSnapshot } from '../geo/locationTracker';
import {
  DecodedMeshPacket,
  DecodedNodeInfo,
  formatNodeHex,
  getNodeByteHash,
  decodeUser,
  decodePosition
} from '../protocol/codec';

const PORTNUM_MAP: Record<number, string> = {
  0: 'UNKNOWN',
  1: 'TEXT_MESSAGE',
  2: 'REMOTE_HARDWARE',
  3: 'POSITION',
  4: 'NODEINFO',
  5: 'ROUTING',
  6: 'ADMIN',
  7: 'COMPRESSED_TEXT',
  8: 'WAYPOINT',
  9: 'AUDIO',
  10: 'DETECTION_SENSOR',
  32: 'REPLY',
  33: 'IP_TUNNEL',
  34: 'PAXCOUNTER',
  64: 'SERIAL',
  65: 'STORE_FORWARD',
  66: 'RANGE_TEST',
  67: 'TELEMETRY',
  68: 'ZPS',
  69: 'SIMULATOR',
  70: 'TRACEROUTE',
  71: 'NEIGHBORINFO',
  73: 'MAP_REPORT'
};

export type FusedPacketHandler = (packet: StoredPacket) => void;
export type NodeUpdateHandler = (node: StoredNode) => void;

/**
 * Calculates great-circle distance between two geographic coordinates in kilometers (Haversine formula).
 *
 * Why: Needed to rank candidates when disambiguating ambiguous 8-bit relay identifiers to the
 * geographically closest physical node.
 *
 * @param lat1 - Latitude of point 1 in degrees
 * @param lon1 - Longitude of point 1 in degrees
 * @param lat2 - Latitude of point 2 in degrees
 * @param lon2 - Longitude of point 2 in degrees
 * @returns Distance in kilometers
 */
export function calculateDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's mean radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Data fusion engine: associates radio packets with GPS position snapshots,
 * tracks known node geographical positions, matches 8-bit relay hashes with known nodes,
 * and persists to IndexedDB.
 */
export class FusionEngine {
  private packetHandlers: FusedPacketHandler[] = [];
  private nodeUpdateHandlers: NodeUpdateHandler[] = [];
  private knownNodesCache: Map<number, StoredNode> = new Map();

  constructor(
    private sessionManager: SessionManager,
    private locationTracker: LocationTracker
  ) {
    this.preloadNodesCache();
  }

  /**
   * Preloads known nodes into memory cache to accelerate 8-bit bitwise relay matching during high traffic.
   */
  async preloadNodesCache(): Promise<void> {
    try {
      const db = await getDb();
      const allNodes = await db.getAll('nodes');
      for (const node of allNodes) {
        const cleanNum = Number(node.num) >>> 0;
        node.num = cleanNum;
        this.knownNodesCache.set(cleanNum, node);
      }
    } catch (err) {
      console.warn('[FusionEngine] Failed to preload nodes cache:', err);
    }
  }

  /**
   * Ingests and stores a discovered NodeInfo packet into the nodes store and memory cache.
   * Extracts the node's own reported position and retains existing coordinates if not provided.
   *
   * @param info - Decoded NodeInfo protobuf message
   */
  async ingestNodeInfo(info: DecodedNodeInfo): Promise<void> {
    const db = await getDb();
    const num = Number(info.num) >>> 0;
    const hexId = formatNodeHex(num);
    const shortName = info.user?.shortName || hexId.substring(hexId.length - 4);
    const longName = info.user?.longName || shortName;
    const byteHash = getNodeByteHash(num);

    let lat: number | null = null;
    let lon: number | null = null;

    if (info.position) {
      const latI = info.position.latitudeI ?? info.position.latitude_i;
      const lonI = info.position.longitudeI ?? info.position.longitude_i;
      if (latI != null && lonI != null && (latI !== 0 || lonI !== 0)) {
        // Meshtastic integer coordinates scale by 1e-7
        lat = latI * 1e-7;
        lon = lonI * 1e-7;
      } else if (info.position.latitude && info.position.longitude) {
        lat = info.position.latitude;
        lon = info.position.longitude;
      }
    }

    // Preserve existing known position if new packet does not contain GPS
    const prevNode = this.knownNodesCache.get(num);
    if (lat === null && prevNode && typeof prevNode.lat === 'number' && typeof prevNode.lon === 'number') {
      lat = prevNode.lat;
      lon = prevNode.lon;
    }

    const nodeRecord: StoredNode = {
      num,
      hexId,
      shortName,
      longName,
      hwModel: info.user?.hwModel || prevNode?.hwModel || 0,
      lastHeard: info.lastHeard || Math.floor(Date.now() / 1000),
      lastSnr: info.snr !== undefined ? info.snr : prevNode?.lastSnr,
      lat,
      lon,
      byteHash
    };

    this.knownNodesCache.set(num, nodeRecord);
    await db.put('nodes', nodeRecord);
    this.emitNodeUpdate(nodeRecord);
  }

  /**
   * Updates an existing node's GPS position from a POSITION_APP (Port 3) packet.
   *
   * @param num - Unsigned 32-bit node ID
   * @param lat - Latitude in degrees
   * @param lon - Longitude in degrees
   */
  async updateNodePosition(num: number, lat: number, lon: number): Promise<void> {
    const cleanNum = Number(num) >>> 0;
    const db = await getDb();
    const hexId = formatNodeHex(cleanNum);
    const existing = this.knownNodesCache.get(cleanNum);

    const updatedNode: StoredNode = {
      num: cleanNum,
      hexId,
      shortName: existing?.shortName || hexId.substring(hexId.length - 4),
      longName: existing?.longName || hexId,
      hwModel: existing?.hwModel || 0,
      lastHeard: Math.floor(Date.now() / 1000),
      lastSnr: existing?.lastSnr,
      lat,
      lon,
      byteHash: getNodeByteHash(cleanNum)
    };

    this.knownNodesCache.set(cleanNum, updatedNode);
    await db.put('nodes', updatedNode);
    this.emitNodeUpdate(updatedNode);
  }

  /**
   * Matches an 8-bit relay identifier against the 32-bit NodeDB using bitwise AND (NodeID & 0xFF).
   *
   * Why: Meshtastic compact routing headers only transmit the lower 8 bits (0..255) of the relaying node.
   * In dense networks, multiple nodes in the database can produce the same 8-bit hash. To prevent
   * misidentification, when multiple candidates match, the node geographically closest to the user's
   * current position is selected as the most probable physical RF last-hop transmitter.
   *
   * @param relayByte - 8-bit relay hash from packet header
   * @param userLat - Optional user/packet latitude in degrees
   * @param userLon - Optional user/packet longitude in degrees
   * @returns Matched StoredNode or null
   */
  findNodeBy8BitRelay(
    relayByte: number,
    userLat?: number | null,
    userLon?: number | null
  ): StoredNode | null {
    if (!relayByte || relayByte === 0) return null;

    const targetHash = relayByte & 0xff;
    const candidates: StoredNode[] = [];

    for (const node of this.knownNodesCache.values()) {
      if ((node.num & 0xff) === targetHash) {
        candidates.push(node);
      }
    }

    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];

    // Disambiguate multiple hash candidates by distance to current user position
    const refLat = userLat ?? this.locationTracker.getLatestSnapshot()?.lat;
    const refLon = userLon ?? this.locationTracker.getLatestSnapshot()?.lon;

    if (typeof refLat === 'number' && typeof refLon === 'number') {
      candidates.sort((a, b) => {
        const hasGeoA = typeof a.lat === 'number' && typeof a.lon === 'number' && (a.lat !== 0 || a.lon !== 0);
        const hasGeoB = typeof b.lat === 'number' && typeof b.lon === 'number' && (b.lat !== 0 || b.lon !== 0);

        if (hasGeoA && hasGeoB) {
          const distA = calculateDistanceKm(refLat, refLon, a.lat!, a.lon!);
          const distB = calculateDistanceKm(refLat, refLon, b.lat!, b.lon!);
          return distA - distB; // Closest first
        }

        // Prefer candidates with known geographic coordinates
        if (hasGeoA && !hasGeoB) return -1;
        if (!hasGeoA && hasGeoB) return 1;

        // Fallback: most recently heard node
        return (b.lastHeard || 0) - (a.lastHeard || 0);
      });
    } else {
      // Fallback without GPS: select most recently heard node
      candidates.sort((a, b) => (b.lastHeard || 0) - (a.lastHeard || 0));
    }

    return candidates[0];
  }

  /**
   * Ingests an incoming MeshPacket, queues it in the 5-second retroactive GPS buffer if needed,
   * performs node resolution and 8-bit relay matching, and saves to IndexedDB.
   *
   * @param rawPacket - Decoded MeshPacket from radio
   */
  async ingestPacket(rawPacket: DecodedMeshPacket): Promise<void> {
    const activeSession = this.sessionManager.getActiveSession();
    if (!activeSession) return;

    // Defense-in-depth: reject non-RF and MQTT packets to keep tracking sessions strictly focused on real LoRa air traffic
    if (rawPacket.rxSnr === undefined || rawPacket.viaMqtt) {
      return;
    }

    // Defense-in-depth: reject internal administrative packets (Port 6: ADMIN_APP)
    if (rawPacket.decoded?.portnum === 6) {
      return;
    }

    // Defense-in-depth: reject stale historical packets older than 30 seconds
    const nowSec = Math.floor(Date.now() / 1000);
    if (rawPacket.rxTime && rawPacket.rxTime > 0 && rawPacket.rxTime < nowSec - 30) {
      console.debug(`[FusionEngine] Rejecting stale historical packet (rxTime=${rawPacket.rxTime}, age=${nowSec - rawPacket.rxTime}s)`);
      return;
    }

    // Use 5-second retroactive buffer: if GPS fix is pending, wait up to 5s before finalizing
    this.locationTracker.resolveLocationForPacket(rawPacket, async (geo: GeoSnapshot | null) => {
      await this.finalizePacketProcessing(rawPacket, geo, activeSession.id);
    });
  }

  private async finalizePacketProcessing(
    rawPacket: DecodedMeshPacket,
    geo: GeoSnapshot | null,
    sessionId: string
  ): Promise<void> {
    const db = await getDb();
    const fromNum = rawPacket.from >>> 0;
    const toNum = rawPacket.to >>> 0;
    const fromHex = formatNodeHex(fromNum);
    const toHex = formatNodeHex(toNum);

    // Resolve sender node name
    const senderNode = this.knownNodesCache.get(fromNum);
    const fromName = senderNode ? senderNode.shortName : fromHex;

    // Resolve 8-bit relay node name via bitwise AND with distance disambiguation
    let relayNodeNum = 0;
    let relayName = 'Direct (None)';
    if (rawPacket.relayNode && rawPacket.relayNode !== 0) {
      relayNodeNum = rawPacket.relayNode >>> 0;
      const matchedRelay = this.findNodeBy8BitRelay(relayNodeNum, geo?.lat, geo?.lon);
      if (matchedRelay) {
        relayName = `${matchedRelay.shortName} (${formatNodeHex(matchedRelay.num)})`;
      } else {
        relayName = `0x${(relayNodeNum & 0xff).toString(16).padStart(2, '0').toUpperCase()}`;
      }
    }

    const portnum = rawPacket.decoded?.portnum || 0;
    const portName = PORTNUM_MAP[portnum] || `PORT_${portnum}`;

    // Auto-register node when a NODEINFO_APP (Port 4) broadcast packet arrives
    if (portnum === 4 && rawPacket.decoded?.payload) {
      try {
        const decodedUser = decodeUser(rawPacket.decoded.payload);
        await this.ingestNodeInfo({
          num: fromNum,
          user: decodedUser,
          snr: rawPacket.rxSnr
        });
      } catch (err) {
        console.warn('[FusionEngine] Failed to decode User from port 4 payload:', err);
      }
    }

    // Auto-update node position when a POSITION_APP (Port 3) packet arrives
    if (portnum === 3 && rawPacket.decoded?.payload) {
      try {
        const decodedPos = decodePosition(rawPacket.decoded.payload);
        const latI = decodedPos.latitudeI ?? decodedPos.latitude_i;
        const lonI = decodedPos.longitudeI ?? decodedPos.longitude_i;
        if (latI != null && lonI != null && (latI !== 0 || lonI !== 0)) {
          const lat = latI * 1e-7;
          const lon = lonI * 1e-7;
          await this.updateNodePosition(fromNum, lat, lon);
        }
      } catch (err) {
        console.warn('[FusionEngine] Failed to decode Position from port 3 payload:', err);
      }
    }

    // Calculate hops taken
    const hopStart = rawPacket.hopStart ?? rawPacket.hopLimit ?? 0;
    const hopLimit = rawPacket.hopLimit ?? 0;
    const hopsTaken = hopStart >= hopLimit ? hopStart - hopLimit : 0;

    // Build payload summary text
    let payloadSummary = '';
    if (rawPacket.decoded?.payload) {
      if (portnum === 1) {
        // Text message
        try {
          payloadSummary = new TextDecoder().decode(rawPacket.decoded.payload);
        } catch {
          payloadSummary = `Bytes: ${rawPacket.decoded.payload.length}`;
        }
      } else {
        payloadSummary = `Port ${portName} (${rawPacket.decoded.payload.length}B)`;
      }
    } else if (rawPacket.encrypted) {
      payloadSummary = `Encrypted (${rawPacket.encrypted.length}B)`;
    }

    const isAck = Boolean(
      rawPacket.decoded?.requestId ||
        rawPacket.decoded?.replyId ||
        (rawPacket.priority !== undefined && rawPacket.priority >= 120)
    );

    const storedPacket: StoredPacket = {
      sessionId,
      timestamp: rawPacket.rxTime ? rawPacket.rxTime * 1000 : Date.now(),
      lat: geo ? geo.lat : null,
      lon: geo ? geo.lon : null,
      accuracy: geo ? geo.accuracy : null,
      hasGeo: geo !== null,
      from: fromNum,
      fromHex,
      fromName,
      to: toNum,
      toHex,
      relayNode: relayNodeNum,
      relayName,
      snr: rawPacket.rxSnr !== undefined ? Number(rawPacket.rxSnr.toFixed(1)) : 0,
      rssi: rawPacket.rxRssi || 0,
      hopLimit,
      hopStart,
      hopsTaken,
      portnum,
      portName,
      payloadSummary,
      isAck,
      packetId: rawPacket.id >>> 0
    };

    // Save into IndexedDB
    const packetId = await db.add('packets', storedPacket);
    storedPacket.id = packetId;

    // Increment session packet count
    await this.sessionManager.incrementPacketCount();

    // Broadcast to UI and map components
    for (const handler of this.packetHandlers) {
      try {
        handler(storedPacket);
      } catch (err) {
        console.error('[FusionEngine] Packet subscriber error:', err);
      }
    }
  }

  onFusedPacket(handler: FusedPacketHandler): void {
    this.packetHandlers.push(handler);
  }

  onNodeUpdate(handler: NodeUpdateHandler): void {
    this.nodeUpdateHandlers.push(handler);
  }

  private emitNodeUpdate(node: StoredNode): void {
    for (const handler of this.nodeUpdateHandlers) {
      try {
        handler(node);
      } catch (err) {
        console.error('[FusionEngine] Node update handler error:', err);
      }
    }
  }

  getKnownNode(nodeNum: number): StoredNode | undefined {
    return this.knownNodesCache.get(nodeNum >>> 0);
  }

  getAllKnownNodes(): StoredNode[] {
    return Array.from(this.knownNodesCache.values());
  }
}
